import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ShieldCheck, Cookie, Sparkles } from "lucide-react";
import { appendConsentLog, type ConsentDecision } from "@/lib/cookieConsentLog";

const CONSENT_KEY = "thiia_cookie_consent";
const CONSENT_VERSION = "2025-04";

type CookiePrefs = {
  version: string;
  essential: true; // always true
  security: boolean; // hCaptcha
  functional: boolean; // sidebar state, language, UI prefs
  analytics: boolean; // future analytics
  decidedAt: string;
};

const defaultPrefs = (overrides: Partial<CookiePrefs> = {}): CookiePrefs => ({
  version: CONSENT_VERSION,
  essential: true,
  security: true,
  functional: true,
  analytics: false,
  decidedAt: new Date().toISOString(),
  ...overrides,
});

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(defaultPrefs());

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) {
        setVisible(true);
        return;
      }
      // Legacy "accepted" / "refused" strings → re-prompt to capture granular consent
      if (raw === "accepted" || raw === "refused") {
        setVisible(true);
        return;
      }
      const parsed = JSON.parse(raw) as CookiePrefs;
      if (parsed?.version !== CONSENT_VERSION) {
        setVisible(true);
        return;
      }
      // Pre-fill the customize dialog with the user's existing choices so
      // re-opening the banner shows their current preferences.
      setPrefs({ ...parsed, version: CONSENT_VERSION });
    } catch {
      setVisible(true);
    }
  }, []);

  // Allow any part of the app (e.g. "Gérer les cookies" link on the
  // Privacy page) to re-open the banner by dispatching a custom event.
  useEffect(() => {
    const reopen = () => {
      try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as CookiePrefs;
          setPrefs({ ...parsed, version: CONSENT_VERSION });
        }
      } catch { /* keep defaults */ }
      setVisible(true);
      setCustomizeOpen(true);
    };
    window.addEventListener("thiia:cookie-consent-reopen", reopen);
    return () => window.removeEventListener("thiia:cookie-consent-reopen", reopen);
  }, []);

  const persist = (next: CookiePrefs, decision: ConsentDecision) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    try {
      window.dispatchEvent(
        new CustomEvent("thiia:cookie-consent", { detail: next })
      );
    } catch { /* noop */ }
    // RGPD proof-of-consent log (local-only).
    appendConsentLog({
      decidedAt: next.decidedAt,
      version: next.version,
      decision,
      prefs: {
        essential: true,
        security: next.security,
        functional: next.functional,
        analytics: next.analytics,
      },
      page: typeof window !== "undefined" ? window.location.pathname : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    });
    setVisible(false);
    setCustomizeOpen(false);
  };

  const acceptAll = () =>
    persist(
      defaultPrefs({ security: true, functional: true, analytics: true }),
      "accept_all",
    );

  const refuseOptional = () =>
    persist(
      defaultPrefs({ security: true, functional: false, analytics: false }),
      "refuse_optional",
    );

  const saveCustom = () =>
    persist({ ...prefs, decidedAt: new Date().toISOString() }, "custom");

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 inset-x-0 z-[100] border-t border-border bg-card/95 backdrop-blur-md shadow-[0_-4px_20px_-4px_hsl(var(--foreground)/0.08)]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center gap-3 px-4 py-3 pb-safe">
          <div className="flex items-start gap-2.5 flex-1 min-w-0">
            <Cookie className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <Trans
                i18nKey="cookies.message"
                components={{ strong: <strong className="text-foreground" /> }}
              />{" "}
              <Link
                to="/privacy"
                className="underline text-primary hover:text-primary/80"
              >
                {t("common.learnMore")}
              </Link>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-9 text-xs px-3"
              onClick={() => setCustomizeOpen(true)}
            >
              {t("cookies.customize", { defaultValue: "Personnaliser" })}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 text-xs px-3"
              onClick={refuseOptional}
            >
              {t("cookies.refuseOptional", { defaultValue: "Refuser" })}
            </Button>
            <Button size="sm" className="h-9 text-xs px-3" onClick={acceptAll}>
              {t("cookies.acceptAll", { defaultValue: "Tout accepter" })}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" />
              {t("cookies.preferences.title", {
                defaultValue: "Préférences cookies",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("cookies.preferences.description", {
                defaultValue:
                  "Choisissez les catégories de cookies que vous autorisez. Les cookies essentiels sont indispensables au fonctionnement du site.",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <PrefRow
              title={t("cookies.cat.essential.title", {
                defaultValue: "Essentiels",
              })}
              description={t("cookies.cat.essential.desc", {
                defaultValue:
                  "Authentification Supabase, session, équilibrage de charge. Toujours actifs.",
              })}
              checked
              disabled
            />
            <PrefRow
              title={t("cookies.cat.security.title", {
                defaultValue: "Sécurité (anti-robot)",
              })}
              description={t("cookies.cat.security.desc", {
                defaultValue:
                  "hCaptcha — protège la création de comptes, la connexion et le mot de passe oublié contre les abus automatisés.",
              })}
              checked={prefs.security}
              onChange={(v) => setPrefs((p) => ({ ...p, security: v }))}
            />
            <PrefRow
              title={t("cookies.cat.functional.title", {
                defaultValue: "Fonctionnels",
              })}
              description={t("cookies.cat.functional.desc", {
                defaultValue:
                  "Mémorisation de la langue, état de la barre latérale, préférences d'affichage du chat et des emails.",
              })}
              checked={prefs.functional}
              onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
            />
            <PrefRow
              title={t("cookies.cat.analytics.title", {
                defaultValue: "Mesure d'audience",
              })}
              description={t("cookies.cat.analytics.desc", {
                defaultValue:
                  "Statistiques anonymisées d'usage des fonctionnalités IA (chat, recherche, génération de contenu) pour améliorer le produit.",
              })}
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
          </div>

          <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-[13px] text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <span>
              {t("cookies.preferences.thirdParties", {
                defaultValue:
                  "Services tiers utilisés : Supabase (auth/données, UE), hCaptcha (anti-bot), Stripe (paiements), Resend (emails). Aucun cookie publicitaire.",
              })}
            </span>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={refuseOptional}>
              {t("cookies.refuseOptional", { defaultValue: "Refuser" })}
            </Button>
            <Button variant="outline" size="sm" onClick={saveCustom}>
              {t("cookies.save", { defaultValue: "Enregistrer mes choix" })}
            </Button>
            <Button size="sm" onClick={acceptAll}>
              {t("cookies.acceptAll", { defaultValue: "Tout accepter" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PrefRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border/60 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <p className="text-[13px] text-muted-foreground leading-relaxed mt-0.5">
          {description}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange?.(Boolean(v))}
        className="mt-1"
      />
    </div>
  );
}
